import type { Component } from 'solid-js';
import { createSignal, Match, onMount, Show, Switch, lazy } from 'solid-js';
import { getClerk } from '@utils/clerk';
import { error, log, warn } from '@utils/logger';
import { addRecentVenue } from '@utils/venueHistory';
import QrCodeIcon from '@iconify-solid/material-symbols/qr-code';
import ChangeCircleOutlineIcon from '@iconify-solid/material-symbols/change-circle-outline';
import ContractEditOutlineIcon from '@iconify-solid/material-symbols/contract-edit-outline';
import SettingsIcon from '@iconify-solid/material-symbols/settings';

import { fetchVenueCodeFromShortCode, fetchVenueBootstrap, type VenueDetails, type EntryDetails } from '@services/recording';
import store, { setBooks, setSessionUser, setTenantSlug, setVenueSlug, setTags, setTemplates } from '@stores/index';
import CodeInput from '@components/CodeInput';
import { networkStatusStore } from '@stores/network';
import MainNav, { Page } from '@components/shell/MainNav';
import AppTitle from '@components/shell/AppTitle';

// Lazy-load heavier pages to reduce initial bundle size and improve first render performance.
const Home = lazy(() => import('@pages/Home'))
const Login = lazy(() => import('@pages/Login'))
const QrCode = lazy(() => import('@pages/QrCode'))
const TemplatesPage = lazy(() => import('@pages/TemplatesPage'))
const RecorderFlow = lazy(() => import('@pages/RecorderFlow'))
const EntryView = lazy(() => import('@pages/EntryView'))


const STORAGE_CODE_KEY = 'tapsafe:venue_code'
const STORAGE_TENANT_KEY = 'tapsafe:tenant_slug'
const STORAGE_VENUE_KEY = 'tapsafe:venue_slug'


const extract_venue_code = (): [string, string] => {
  const searchParams = new URLSearchParams(window.location.search);
  const b64FromUrl = searchParams.get('c')

  // If URL contains a venue code, decode and persist tenant/venue separately.
  if (b64FromUrl) {
    try {
      const textCode = atob(b64FromUrl)
      const parts = textCode.split('/')
      const tenant = parts[0] || ''
      const venue = parts[1] || ''
      // persist both combined code (compat) and separate values
      try { localStorage.setItem(STORAGE_CODE_KEY, b64FromUrl) } catch (_) { }
      try { localStorage.setItem(STORAGE_TENANT_KEY, tenant) } catch (_) { }
      try { localStorage.setItem(STORAGE_VENUE_KEY, venue) } catch (_) { }
      return [tenant, venue]
    } catch (err) {
      warn('Invalid venue code in URL', err)
    }
  }

  // If no URL param, attempt to read separated tenant/venue from localStorage
  try {
    const tenant = localStorage.getItem(STORAGE_TENANT_KEY) || ''
    const venue = localStorage.getItem(STORAGE_VENUE_KEY) || ''
    if (tenant && venue) return [tenant, venue]
  } catch (_) { }

  // Backwards compatibility: fall back to combined stored code
  try {
    const stored = localStorage.getItem(STORAGE_CODE_KEY)
    if (stored) {
      const textCode = atob(stored)
      const parts = textCode.split('/')
      return [parts[0] || '', parts[1] || '']
    }
  } catch (err) {
    warn('Failed to decode stored venue code', err)
    try { localStorage.removeItem(STORAGE_CODE_KEY) } catch (_) { }
  }

  return ['', '']
}


const App: Component = () => {
  const [tenant, venue] = extract_venue_code();
  // populate global store with tenant/venue so components can consume them
  try { setTenantSlug(tenant) } catch (_) { }
  try { setVenueSlug(venue) } catch (_) { }
  const [venue_details, setVenueDetails] = createSignal<VenueDetails | null>(null);
  const [authenticated, setAuthenticated] = createSignal<boolean | null>(null);
  const [sessionData, setSessionData] = createSignal<any | null>(null);
  const [latestEntries, setLatestEntries] = createSignal<EntryDetails[]>([]);
  const [accessDenied, setAccessDenied] = createSignal<boolean>(false);
  const [loading, setLoading] = createSignal(true);
  const [currentPage, setCurrentPage] = createSignal<Page>('home');
  const [selectedEntry, setSelectedEntry] = createSignal<import('@services/recording').EntryDetails | null>(null);
  const [codeDigits, setCodeDigits] = createSignal<string[]>(Array(6).fill(''));
  const [codeError, setCodeError] = createSignal<string | null>(null);
  const [codeSubmitting, setCodeSubmitting] = createSignal(false);
  const PAGE_KEY = 'recorder_current_page'

  // restore persisted page if present (entry view is not persisted)
  try {
    const saved = localStorage.getItem(PAGE_KEY)
    if (saved === 'home' || saved === 'qr' || saved === 'recorder' || saved === 'templates') {
      setCurrentPage(saved as any)
    }
  } catch (_) { }

  const setPage = (p: 'home' | 'switch' | 'qr' | 'recorder' | 'templates' | 'entry') => {
    setCurrentPage(p);
    try { localStorage.setItem(PAGE_KEY, p) } catch (_) { }
  };

  onMount(async () => {
    const checkAuth = async () => {
      try {
        const clerk = await getClerk();
        await clerk.load();

        // Handle Clerk redirect callbacks (e.g. email link sign-in) and keep the user on the same page.
        if (typeof clerk.handleRedirectCallback === 'function') {
          try {
            await clerk.handleRedirectCallback({}, () => {
              // No-op: we manage routing ourselves.
            });
          } catch (err) {
            // Ignore; we still want to check whether the user is signed in.
          }
        }

        // Clean up Clerk query params from the URL to avoid showing them in the address bar.
        const url = new URL(window.location.href);
        const params = url.searchParams;
        const clerkParams = ['__clerk_status', '__clerk_created_session', '__clerk_ticket'];
        let removed = false;
        clerkParams.forEach((p) => {
          if (params.has(p)) {
            params.delete(p);
            removed = true;
          }
        });
        if (removed) {
          window.history.replaceState({}, '', `${url.pathname}${params.toString() ? `?${params.toString()}` : ''}${url.hash}`);
        }

        log(clerk.isSignedIn);
        setAuthenticated(clerk.isSignedIn);
      } catch (err) {
        warn('Auth check failed', err);
        setAuthenticated(false);
      }
    };

    const loadAppData = async () => {
      await checkAuth();

      // After exchanging token, request bootstrap payload from the new combined endpoint.
      try {
        const bootstrap = await fetchVenueBootstrap(tenant, venue);
        if (!bootstrap) {
          setAccessDenied(true);
          return;
        }

        setSessionData(bootstrap.me);
        try { setSessionUser(bootstrap.me) } catch (_) { }

        setVenueDetails(bootstrap.venue_details);
        addRecentVenue({ tenant, venue, name: bootstrap.venue_details.name });

        setBooks(bootstrap.books || []);

        const mappedTags = (bootstrap.tags || []).map((t: any) => ({
          id: t.id || t.tag_ref || t.ref || String(t.key || t.name),
          label: t.label || t.name || '',
          level: (t.severity ?? t.level) || 0,
          icon_name: t.icon || t.icon_name || t.iconName || ''
        }));
        setTags(mappedTags);

        const mappedTemplates = (bootstrap.templates || []).map((t: any) => ({
          template: t.template,
          name: t.name,
          logbook: t.logbook,
          initial_state: t.initial_state || undefined,
          auto_submit: t.auto_submit || undefined
        }))
        setTemplates(mappedTemplates);

        // Bootstrapped latest entries now come as flattened EntryDetails, so use directly.
        setLatestEntries(bootstrap.latest || []);
      } catch (err) {
        setAccessDenied(true);
      }
    };

    try {
      await loadAppData();
    } finally {
      setLoading(false);
    }
  });

  const onClickQrCode = () => {
    if (currentPage() === 'qr') {
      setPage('home')
    } else {
      setPage('qr');
    }
  }

  const resolveShortCode = async (shortCode: string) => {
    if (shortCode.length !== 6) {
      return;
    }

    if (codeSubmitting()) return;
    setCodeError(null);
    setCodeSubmitting(true);

    try {
      const encoded = await fetchVenueCodeFromShortCode(shortCode);
      if (!encoded) throw new Error('Invalid response');

      try { localStorage.setItem(STORAGE_CODE_KEY, encoded) } catch (_) { }

      // Persist derived tenant/venue; this mirrors the behavior when a full code is passed via URL.
      try {
        const decoded = atob(encoded);
        const parts = decoded.split('/');
        if (parts[0] && parts[1]) {
          try { localStorage.setItem(STORAGE_TENANT_KEY, parts[0]) } catch (_) { }
          try { localStorage.setItem(STORAGE_VENUE_KEY, parts[1]) } catch (_) { }
        }
      } catch (_) {
        // ignore
        setCodeError('Failed to decode venue code. Please try again or contact support.');
        setCodeSubmitting(false);
        return;
      }

      window.location.reload();
    } catch (err) {
      setCodeError('Invalid venue code. Please check and try again.');
      setCodeSubmitting(false);
    }
  };

  const onOpenRecorder = () => {
    setPage('templates')
  }

  const onClickHome = () => {
    setPage('home');
  }

  const onMainNavNavigate = (value: Page) => {
    setPage(value)
  }

  return (
    <div class="bg-zinc-800 h-full w-full flex flex-col items-stretch m-0 p-0 overflow-hidden">
      <Show when={loading()}>
        <div class="bg-zinc-800 h-full w-full flex flex-col items-center justify-center gap-4">
          <img src="/pwa-512x512.png" alt="TapSafe logo" class="h-32 w-32" />
          <p class="text-white text-lg">Loading…</p>
        </div>
      </Show>

      <Show when={networkStatusStore.isOnline === false}>
        <div class="bg-red-600 text-white p-2 text-center">
          You are currently offline. Some features may be unavailable until you regain connectivity.
        </div>
      </Show>

      <Show when={!loading()}>
        <Show
          when={tenant && venue}
          fallback={
            <CodeInput
              onChange={(newValue) => resolveShortCode(newValue)}
              error={codeError()}
              submitting={codeSubmitting()}
            />
          }
        >
          <Switch>
            <Match when={authenticated() === false}>
              <main class="grow flex min-h-0">
                <Login />
              </main>
            </Match>

            <Match when={authenticated() === true}>
              <Show when={!accessDenied()} fallback={<div class="bg-zinc-800 h-full w-full flex items-center justify-center"><p class="text-white text-lg">You do not have access to this venue.</p></div>}>
                <Show when={venue_details()}>
                  <MainNav
                    userCapabilities={
                      {
                        isManager: store.sessionUser?.capabilities?.is_manager || false,
                        isOwner: store.sessionUser?.capabilities?.is_owner || false
                      }
                    }
                    isOpen={true}
                    activeValue={currentPage() === 'entry' ? 'home' : currentPage()}
                    onNavigate={(item) => onMainNavNavigate(item.value)}
                    root={
                      {
                        label: 'Main',
                        value: 'home',
                        children: [
                          { label: 'Record', value: 'home', icon: () => <ContractEditOutlineIcon height="1em" /> },
                          { label: 'Manage', value: 'manage', requiredCapabilities: { isManager: true }, icon: () => <SettingsIcon height="1em" /> },
                          { label: 'QR Code', value: 'qr', icon: () => <QrCodeIcon height="1em" /> },
                          { label: 'Switch Venue', value: 'switch', icon: () => <ChangeCircleOutlineIcon height="1em" /> },
                        ]
                      }
                    }
                    logo={
                      <AppTitle
                        tenant_slug={store.tenant_slug || ''}
                        venue_slug={store.venue_slug || ''}
                        venue_name={venue_details()?.name || ''}
                      />
                    }
                  />
                  <main class="grow flex shrink flex-col min-h-0">
                    <Switch>
                      <Match when={currentPage() === 'home'}>
                        <Home
                          venue_details={venue_details()!}
                          latest_entries={latestEntries()}
                          onOpenRecorder={onOpenRecorder}
                          onResumeRecorder={() => setPage('recorder')}
                          onEntryClick={(entry) => { setSelectedEntry(() => entry); setPage('entry') }}
                          show_data_select={true}
                          show_search={true}
                        />
                      </Match>
                      <Match when={currentPage() === 'qr'}>
                        <QrCode
                          venue_name={venue_details()?.name || ""}
                          venue_short_code={venue_details()?.short_code}
                        />
                      </Match>
                      <Match when={currentPage() === 'templates'}>
                        <TemplatesPage onLaunch={() => setPage('recorder')} onCancel={() => setPage('home')} />
                      </Match>
                      <Match when={currentPage() === 'recorder'}>
                        <RecorderFlow onCancel={() => setCurrentPage('home')} />
                      </Match>
                      <Match when={currentPage() === 'entry' && selectedEntry() !== null}>
                        <EntryView entry={selectedEntry()!} onBack={() => setPage('home')} />
                      </Match>
                      <Match when={currentPage() === 'switch'}>
                        <CodeInput
                          onChange={(newValue) => resolveShortCode(newValue)}
                          error={codeError()}
                          submitting={codeSubmitting()}
                        />
                      </Match>
                      <Match when={currentPage() === 'manage'}>
                        <div class="p-4 text-white">Management features coming soon!</div>
                      </Match>
                    </Switch>
                  </main>
                </Show>
              </Show>
            </Match>
          </Switch>
        </Show>
      </Show>
    </div>
  );
};

export default App;
